import { gql } from '@apollo/client';
import { LoadingIcon } from '@assets/icons';
import { Form, StandardFormButtons } from '@components/form';
import { Group } from '@components/layout';
import { useModal, type UseModalProps } from '@components/modal';
import { useEditVolumeMutation, useGetEditVolumeDetailsQuery } from '@generated/graphql';
import { Stack } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import type { FormikHelpers } from 'formik';
import posthog from 'posthog-js';
import * as yup from 'yup';
import {
  VolumeDescriptionField,
  VolumeNameField,
  VolumePermissionField,
  VolumeTagsField,
  volumeDescriptionSchema,
  volumeNameSchema,
  volumePermissionSchema,
  volumeTagsSchema,
} from './fields';

gql`
  query GetEditVolumeDetails($volumeId: String!) {
    getVolumes(volumeId: $volumeId) {
      volumeId
      name
      description
      permission
      tags
    }
  }
`;

const validationSchema = yup.object({
  name: volumeNameSchema,
  permission: volumePermissionSchema,
  description: volumeDescriptionSchema,
  tags: volumeTagsSchema,
});
type FormValues = yup.InferType<typeof validationSchema>;

type EditVolumeModalProps = {
  volumeId: string;
  onEdit?: VoidFunction;
  onClose: VoidFunction;
};

const EditVolumeModal = ({ volumeId, onEdit, onClose }: EditVolumeModalProps) => {
  const { createAsyncNotification } = useNotifications();

  const { data: volumeData, loading: volumeLoading } = useGetEditVolumeDetailsQuery({
    variables: {
      volumeId,
    },
    skip: !volumeId,
  });
  const volume = volumeData?.getVolumes?.[0];

  const [editVolumeMutation] = useEditVolumeMutation();

  const editVolume = async (values: FormValues, { setStatus }: FormikHelpers<FormValues>) => {
    posthog.capture('agent-studio:user_edit-volume_submitted', { volumeId });
    await editVolumeMutation({
      variables: {
        volumeId,
        name: values.name,
        description: values.description,
        permission: values.permission,
        tags: values.tags,
      },
      onCompleted: () => {
        posthog.capture('agent-studio:user_edit-volume_completed', { volumeId });
        onEdit?.();
        onClose();
      },
      onError: (error) => {
        posthog.capture('agent-studio:user_edit-volume_error', { volumeId, error: error.message });
        setStatus({ error: error.message });
        throw error;
      },
    });
  };

  const handleSubmission = createAsyncNotification('Volume was updated successfully', editVolume, {
    loadingMessage: 'Updating volume...',
  });

  if (volumeLoading) {
    return (
      <Group alignItems="center" justifyContent="center" height="200px">
        <LoadingIcon size={12} />
      </Group>
    );
  }

  return (
    <Form
      enableReinitialize
      validateOnBlur={false}
      validationSchema={validationSchema}
      initialValues={{
        name: volume?.name || '',
        description: volume?.description || '',
        permission: volume?.permission || '',
        tags: volume?.tags || [],
      }}
      onSubmit={handleSubmission}
    >
      <Stack>
        <VolumeNameField />
        <VolumePermissionField />
        <VolumeDescriptionField />
        <VolumeTagsField />
        <StandardFormButtons onClose={onClose} submitLabel="Save" />
      </Stack>
    </Form>
  );
};

export const useEditVolumeModal = ({
  componentProps,
  modalProps,
}: UseModalProps<typeof EditVolumeModal>) => {
  return useModal({
    component: EditVolumeModal,
    componentProps,
    modalProps: { title: 'Edit volume', ...modalProps },
  });
};
